import React from "react";
import ReactDOM from "react-dom";
import { BrowserRouter as Router, Switch, Route } from "react-router-dom";

// Utilities
import {
  getCurrentEmailId,
  getIsOfficeJs,
  getIsOutlook,
  getIsWord,
  getIsExcel,
  getIsPowerPoint,
} from "./util-helpers/office";
import { nrPageAction } from "./util-helpers/newrelic";
import {
  initializeOffice,
  waitForOfficeInitialization,
} from "./util-helpers/officeInitialization";

// Child Components
import MsalAuth from "./Msal/MsalAuth";
import MsalLogout from "./Msal/MsalLogout";
import MsalLogoutSuccess from "./Msal/MsalLogoutSuccess";
import MsalLanding from "./Msal/MsalLanding";
import Progress from "./Progress";
import Snackbar from "./Snackbar";
import ErrorBoundary from "./ErrorBoundary";
import { AddInStorage } from "./storage/AddInStorage";
import App from "./App";
import { AuthProvider } from "./fvid-auth/authContext";
import ApiProvider from "./util-api/ApiProvider";

const loadApp = () => {
  try {
    // get querystring from url
    const urlParams = new URLSearchParams(window.location.search);
    const sk = urlParams.get("sk");
    if (sk) {
      AddInStorage.restoreData(sk);
    }

    let isAppInitialized = false;
    let isOutlook = false;
    let isWord = false;
    let isExcel = false;
    let isPowerPoint = false;

    const render = async () => {
      let currentEmailId;
      try {
        currentEmailId = await getCurrentEmailId();
      } catch (error) {
        nrPageAction("outlookLoad", {
          message: `currentEmailId Error: ${error}`,
        });
      }

      ReactDOM.render(
        <ErrorBoundary>
          <Snackbar normalTimeout={5000} errorTimeout={10000}>
            <AuthProvider>
              <ApiProvider>
                <Router>
                  <Switch>
                    {/* MSAL routes */}
                    <Route path="/msallogoutsuccess">
                      <MsalLogoutSuccess />
                    </Route>
                    <Route path="/msallogout">
                      <MsalLogout />
                    </Route>
                    <Route path="/msalauth">
                      <MsalAuth />
                    </Route>
                    <Route path="/auth">
                      <MsalAuth />
                    </Route>
                    <Route path="/msalrefresh">
                      <MsalLanding />
                    </Route>

                    {/* Show loading before Landing and Taskpane if office js is not ready */}
                    {!isAppInitialized && (
                      <Route>
                        <Progress
                          logo="assets/fv-logo.svg"
                          message="Office.js is Loading..."
                          errorMsg="Loading Office.js Failed"
                        />
                      </Route>
                    )}
                    <Route>
                      <App
                        isOutlook={isOutlook}
                        isWord={isWord}
                        isExcel={isExcel}
                        isPowerPoint={isPowerPoint}
                        currentEmailId={currentEmailId}
                      />
                    </Route>
                  </Switch>
                </Router>
              </ApiProvider>
            </AuthProvider>
          </Snackbar>
        </ErrorBoundary>,
        document.getElementById("app-content")
      );
    };

    const loadOffice = (count = 0) => {
      const timeout = 5000;
      // Render office is loading if it is past the initial load
      if (count === 1) render();
      if (count <= 5) {
        if (global.Office) {
          (async () => {
            try {
              await waitForOfficeInitialization();

              if (getIsOfficeJs()) {
                isOutlook = getIsOutlook();
                isWord = getIsWord();
                isExcel = getIsExcel();
                isPowerPoint = getIsPowerPoint();
              }

              isAppInitialized = true;
              render();
            } catch (error) {
              // eslint-disable-next-line no-console
              console.error(`Office initialization Error: ${error}`);
              nrPageAction("outlookLoad", {
                message: `Office initialization Error: ${error}`,
              });
              setTimeout(() => {
                loadOffice(count + 1);
              }, timeout);
            }
          })();
        } else {
          // eslint-disable-next-line no-console
          console.error("No `Office` global found.");
          nrPageAction("outlookLoad", { message: "No Office global" });
          setTimeout(() => {
            loadOffice(count + 1);
          }, timeout);
        }
      }
    };

    // Start Office.js initialization as early as possible
    initializeOffice().catch((error) => {
      console.error("Failed to initialize Office.js:", error);
      nrPageAction("outlookLoad", {
        message: `Office initialization failed: ${error}`,
      });
    });

    loadOffice();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("error: ", error);
    nrPageAction("outlookLoad", { message: `General load ${error}` });
  }
};

export default loadApp;
